// apps/merchant-v3/src/lib/websocket/wsClient.ts
//
// Phase 6 Wave 12 Task 40 — Full WebSocket multiplexer for the merchant
// portal. UPGRADES the Wave 6 Task 17 stub at `@/services/wsClient`.
// The legacy module remains as a thin re-export shim so existing
// callers (`useOrders`, `useWallet`, `useBillingSummary`,
// `PaymentLandingPage`) continue to compile without churn.
//
// What lands here vs. the Wave 6 stub:
//
//   1. Auth handshake — every connect() round-trip POSTs
//      /api/v1/auth/ws-ticket first (the BE returns a short-lived JWT
//      bound to the merchant); the ticket is sent as the
//      `Sec-WebSocket-Protocol` sub-protocol so the BE can validate
//      the upgrade without the FE shipping the long-lived auth cookie
//      across the upgrade boundary.
//   2. Reconnect with explicit backoff schedule per the task spec
//      (1s → 2s → 5s → 10s → cap 30s, reset on a clean OPEN). The
//      stub's geometric-2x curve is replaced with this fixed ladder
//      because the BE's connection-rate-limit window is 5s (so we
//      MUST NOT hammer the upgrade endpoint sub-second).
//   3. Heartbeat — a `{ op: 'ping' }` frame is emitted every 30s so
//      the gateway / load-balancer (NGINX 60s idle timeout) does not
//      garbage-collect the socket mid-stream.
//   4. Multiplex — channels follow the canonical pattern
//      `merchant.${merchantId}.{notifications|orders|wallet|billing}`.
//      The frame format `{ channel, payload }` is preserved so the
//      Wave 6 wire shape remains backward-compatible with every
//      existing handler.
//   5. SSR / non-browser safety — every public function is a no-op
//      when `window.WebSocket` is missing (Node, vitest jsdom).
//
// Decisions covered:
//    (in-app live delivery via the merchant.${id}.notifications
//   channel), (auth-ticket handshake — short-lived JWT issued
//   by POST /auth/ws-ticket so the long-lived session cookie never
//   crosses the WS upgrade boundary).
//
// vg-binding: PLAN:task-40
// vg-binding: P6.D-06
// vg-binding: P6.D-118
// vg-binding: P6.D-120
// vg-binding: P6.D-121
// vg-binding: P6.D-174
// vg-binding: P6.D-175
// vg-binding: G-65
// vg-binding: G-66
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

/* eslint-disable @typescript-eslint/no-explicit-any */

import type { OrderWsMessage } from '@/types/order';
import type { WalletWsMessage } from '@/types/wallet';
import type { NotificationWsMessage } from '@/types/notification';

// ---- public types --------------------------------------------------------

/**
 * Discriminated union of every WebSocket message kind currently consumed
 * by the SPA. Each owning wave appends its own message types to this
 * union as they land. Wave 12 Task 40 extends with the
 * `NotificationWsMessage` family so the bell + Center stay live.
 */
export type WSMessage = OrderWsMessage | WalletWsMessage | NotificationWsMessage;

export type WSHandler = (msg: WSMessage) => void;

export interface WSClient {
  subscribe(channel: string, handler: WSHandler): () => void;
  /** Force-close the underlying socket — primarily for tests / logout. */
  close(): void;
}

/** Shape of the POST /api/v1/auth/ws-ticket success payload. */
export interface WsTicket {
  /** Short-lived (≤60s) one-time hex ticket bound to the merchant id + IP.
   *  dbg-20260702-cors: the BE payload field is `ticket` — the FE previously
   *  declared (and read) `token`, which does not exist, so the upgrade was
   *  attempted with `undefined` and always rejected. */
  ticket: string;
  /** ISO-8601 absolute expiry — the FE never reuses an expired ticket. */
  expiresAt: string;
  /** Canonical upgrade URL returned by the BE (e.g. wss://api-v3…/ws).
   *  Preferred over the build-time VITE_WS_URL when present. */
  wsUrl?: string;
}

// ---- module-local state --------------------------------------------------

const ENV_WS = import.meta.env.VITE_WS_URL as string | undefined;
const ENV_API_BASE = (import.meta.env.VITE_API_BASE_URL ??
  import.meta.env.VITE_API_URL) as string | undefined;
const API_BASE_URL =
  ENV_API_BASE && ENV_API_BASE.length > 0 ? ENV_API_BASE.replace(/\/$/, '') : '';
const WS_URL = ENV_WS && ENV_WS.length > 0 ? ENV_WS : 'ws://localhost:3001/ws';

/** Fixed backoff ladder per the task spec — 1s, 2s, 5s, 10s, 30s cap.
 *  Indexed by attempt count; clamped at the last entry. */
const BACKOFF_LADDER_MS: ReadonlyArray<number> = [
  1_000, 2_000, 5_000, 10_000, 30_000,
];

/** Heartbeat interval. NGINX defaults to 60s idle GC; 30s gives us a
 *  comfortable safety margin (connection-keepalive). */
const HEARTBEAT_INTERVAL_MS = 30_000;

/** Linger window after the last unsubscribe — keeps the socket warm
 *  during rapid route-change navigation so the auth handshake does
 *  not refire on every page transition. */
const LINGER_MS = 5_000;

/** Ticket-fetch URL — built once so test harnesses can stub fetch easily. */
const TICKET_PATH = '/api/v1/auth/ws-ticket';
const TICKET_URL = API_BASE_URL ? API_BASE_URL + TICKET_PATH : TICKET_PATH;
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';

let socket: WebSocket | null = null;
let attemptCount = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let lingerTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let connecting = false;

/** Last issued ticket — re-used across reconnects until expiry. The BE
 *  rotates the ticket on every fresh upgrade so even a leaked sub-
 *  protocol cannot replay. */
let cachedTicket: WsTicket | null = null;

const channels = new Map<string, Set<WSHandler>>();

// ---- helpers --------------------------------------------------------------

function hasWebSocket(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).WebSocket === 'function'
  );
}

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function clearLingerTimer(): void {
  if (lingerTimer) {
    clearTimeout(lingerTimer);
    lingerTimer = null;
  }
}

function clearHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function totalSubscriberCount(): number {
  let n = 0;
  for (const set of channels.values()) n += set.size;
  return n;
}

function dispatch(channel: string, payload: WSMessage): void {
  const set = channels.get(channel);
  if (!set) return;
  for (const h of set) {
    try {
      h(payload);
    } catch {
      // Handlers must not break the dispatcher — swallow individual errors.
    }
  }
}

/** Backoff window for the current attempt — clamps at the last entry. */
function nextBackoffMs(): number {
  const idx = Math.min(attemptCount, BACKOFF_LADDER_MS.length - 1);
  return BACKOFF_LADDER_MS[idx] ?? 30_000;
}

/** Returns true when the cached ticket is still safely usable
 *  (>= 5s of TTL remaining). */
function ticketIsFresh(t: WsTicket | null): t is WsTicket {
  if (!t) return false;
  const exp = Date.parse(t.expiresAt);
  if (Number.isNaN(exp)) return false;
  return exp - Date.now() > 5_000;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + escaped + '=([^;]*)'),
  );
  return match && typeof match[1] === 'string' ? decodeURIComponent(match[1]) : null;
}

function readCsrfToken(): string | null {
  return readCookie(CSRF_COOKIE_NAME) ?? readCookie('csrf_token');
}

/**
 * POST /api/v1/auth/ws-ticket — short-lived JWT bound to the merchant
 * + the client IP. Returns the unwrapped ticket payload.
 *
 * We hand-roll fetch here (instead of importing `apiClient`) because
 * `lib/websocket` MUST stay free of cyclic dependencies — `apiClient`
 * may eventually want to use the WS client to broadcast retry events.
 */
async function fetchTicket(): Promise<WsTicket | null> {
  if (typeof fetch === 'undefined') return null;
  try {
    const csrf = readCsrfToken();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    if (csrf) {
      headers['X-XSRF-TOKEN'] = csrf;
    }
    const res = await fetch(TICKET_URL, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ scope: 'user' }),
    });
    if (!res.ok) return null;
    // dbg-20260702-cors iter2 — the BE envelope is {success:true, data}
    // (wsTicket.handler.ts); this client checked `ok` which never exists,
    // so every mint parsed as failure -> backoff retry loop that minted a
    // fresh (one-time) ticket every cycle and never opened a socket.
    const body = (await res.json()) as {
      success?: boolean;
      ok?: boolean;
      data?: WsTicket;
    } | null;
    if (!body || (body.success !== true && body.ok !== true) || !body.data) {
      return null;
    }
    return body.data;
  } catch {
    return null;
  }
}

async function getTicket(): Promise<WsTicket | null> {
  if (ticketIsFresh(cachedTicket)) return cachedTicket;
  const fresh = await fetchTicket();
  cachedTicket = fresh;
  return fresh;
}

function startHeartbeat(): void {
  clearHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ op: 'ping', t: Date.now() }));
    } catch {
      /* ignore — next reconnect will restart the heartbeat */
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function sendSubscribe(channel: string): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  try {
    socket.send(JSON.stringify({ op: 'subscribe', channel }));
  } catch {
    /* ignore */
  }
}

function sendUnsubscribe(channel: string): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  try {
    socket.send(JSON.stringify({ op: 'unsubscribe', channel }));
  } catch {
    /* ignore */
  }
}

function teardownIfIdle(): void {
  if (totalSubscriberCount() > 0) return;
  clearLingerTimer();
  lingerTimer = setTimeout(() => {
    lingerTimer = null;
    if (totalSubscriberCount() === 0 && socket) {
      try {
        socket.close();
      } catch {
        /* ignore */
      }
      socket = null;
      attemptCount = 0;
      clearReconnectTimer();
      clearHeartbeat();
    }
  }, LINGER_MS);
}

async function connect(): Promise<void> {
  if (!hasWebSocket()) return;
  if (connecting) return;
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  connecting = true;
  // Auth handshake — without a fresh ticket the BE will reject the
  // upgrade with HTTP 401, so we short-circuit + schedule a retry.
  const ticket = await getTicket();
  if (!ticket) {
    connecting = false;
    if (totalSubscriberCount() > 0) scheduleReconnect();
    return;
  }

  let s: WebSocket;
  try {
    // dbg-20260702-cors: the BE upgrade preHandler consumes the ticket
    // EXCLUSIVELY from the `?ticket=<hex>` query param (Redis GETDEL) —
    // it never reads the Sec-WebSocket-Protocol header this client used
    // to advertise. Sending the ticket as a sub-protocol meant every
    // upgrade was rejected 401 → endless reconnect loop hammering
    // POST /auth/ws-ticket. Use the query param + keep only the
    // wire-format tag as sub-protocol. Prefer the BE-issued wsUrl
    // (absolute, environment-correct) over build-time VITE_WS_URL.
    const base = ticket.wsUrl && ticket.wsUrl.length > 0 ? ticket.wsUrl : WS_URL;
    const sep = base.includes('?') ? '&' : '?';
    s = new WebSocket(
      `${base}${sep}ticket=${encodeURIComponent(ticket.ticket)}`,
      ['merchant.v1'],
    );
    // Tickets are ONE-TIME (BE consumes via Redis GETDEL on upgrade) —
    // a reconnect must never replay this one, so drop the cache now.
    cachedTicket = null;
  } catch {
    connecting = false;
    scheduleReconnect();
    return;
  }
  socket = s;

  s.addEventListener('open', () => {
    attemptCount = 0;
    connecting = false;
    startHeartbeat();
    // Re-subscribe to all known channels so a reconnect is transparent.
    for (const channel of channels.keys()) {
      try {
        s.send(JSON.stringify({ op: 'subscribe', channel }));
      } catch {
        /* ignore — next reconnect will retry */
      }
    }
  });

  s.addEventListener('message', (ev) => {
    if (typeof ev.data !== 'string') return;
    let frame:
      | { channel?: string; payload?: WSMessage; op?: string }
      | null;
    try {
      frame = JSON.parse(ev.data) as {
        channel?: string;
        payload?: WSMessage;
        op?: string;
      };
    } catch {
      return;
    }
    if (!frame) return;
    // Server-side keepalive / ack — silently swallow; no consumer cares.
    if (frame.op === 'pong' || frame.op === 'ack') return;
    const channel = frame.channel;
    const payload = frame.payload;
    if (!channel || !payload) return;
    dispatch(channel, payload);
  });

  const onCloseOrError = (): void => {
    socket = null;
    connecting = false;
    clearHeartbeat();
    // The BE rotates tickets on every upgrade — invalidate our cache so
    // the next connect() round-trip re-fetches before the upgrade.
    cachedTicket = null;
    if (totalSubscriberCount() > 0) scheduleReconnect();
  };
  s.addEventListener('close', onCloseOrError);
  s.addEventListener('error', onCloseOrError);
}

function scheduleReconnect(): void {
  clearReconnectTimer();
  const delay = nextBackoffMs();
  attemptCount += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    void connect();
  }, delay);
}

// ---- public API ----------------------------------------------------------

/**
 * Subscribe `handler` to messages on `channel`. Returns an unsubscribe
 * function that, when called, removes the handler and (if no handlers
 * remain) gracefully closes the socket after a short linger window.
 *
 * Channel naming follows the canonical pattern
 * `merchant.${merchantId}.{notifications|orders|wallet|billing}`. The
 * caller is responsible for composing the channel string — this module
 * is intentionally agnostic to the merchant id so the same instance
 * can serve admin / vendor portals when those land.
 *
 * Safe to call in non-browser environments — returns a no-op
 * unsubscriber.
 */
export function subscribe(channel: string, handler: WSHandler): () => void {
  if (!hasWebSocket()) {
    return () => {
      /* no-op */
    };
  }

  clearLingerTimer();
  let handlers = channels.get(channel);
  const isFirstForChannel = !handlers;
  if (!handlers) {
    handlers = new Set<WSHandler>();
    channels.set(channel, handlers);
  }
  handlers.add(handler);

  if (!socket) {
    void connect();
  } else if (isFirstForChannel && socket.readyState === WebSocket.OPEN) {
    sendSubscribe(channel);
  }

  let active = true;
  return () => {
    if (!active) return;
    active = false;
    const set = channels.get(channel);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) {
      channels.delete(channel);
      sendUnsubscribe(channel);
    }
    teardownIfIdle();
  };
}

/**
 * `publish()` is a no-op on the FE — every state-changing path goes
 * through the REST API (the BE then broadcasts the resulting frame
 * back over WS). Exported for symmetry with the BE-side helper so
 * future tests can spy on the call without an `if (publish)` guard.
 */
export function publish(_channel: string, _payload: unknown): void {
  /* no-op — see docstring */
}

/** Force-close the socket and reset all module-local state. Primarily
 *  used by `AuthProvider.logout()` so the next session starts clean. */
export function close(): void {
  clearReconnectTimer();
  clearLingerTimer();
  clearHeartbeat();
  channels.clear();
  cachedTicket = null;
  attemptCount = 0;
  connecting = false;
  if (socket) {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
    socket = null;
  }
}

export const wsClient: WSClient = { subscribe, close };

export default wsClient;
